<template>
	<div class="w-full">
		<!-- Header -->
		<div class="mb-6">
			<h1
				class="text-2xl lg:text-3xl font-bold text-text-primary mb-2 tracking-tight"
			>
				Notifications
			</h1>
			<p class="text-sm lg:text-base text-text-secondary">
				Stay updated with your latest activity and alerts
			</p>
		</div>

		<!-- Notification Details Modal -->
		<Modal
			:show="showNotification"
			content_class="w-full max-w-2xl mx-auto"
		>
			<template slot="content" v-if="selectedNotification">
				<div
					class="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
				>
					<div class="flex items-start justify-between mb-6">
						<div class="flex-1 min-w-0">
							<div class="flex items-center gap-3 mb-2">
								<div
									class="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0"
								>
									<i
										class="fas fa-bell text-blue-600 text-base"
									></i>
								</div>
								<h2
									class="text-xl lg:text-2xl font-bold text-gray-900 tracking-tight truncate"
								>
									{{ selectedNotification.subject }}
								</h2>
							</div>
							<div
								class="flex items-center gap-2 text-sm text-gray-500 ml-13"
							>
								<i class="fas fa-clock text-xs"></i>
								<span>{{
									moment(
										selectedNotification.notification_date,
									).format('Do MMM YYYY HH:mm')
								}}</span>
							</div>
						</div>
						<button
							class="text-gray-500 hover:text-gray-700 transition-colors p-2 flex-shrink-0"
							@click="closeModal()"
						>
							<i class="fas fa-times text-lg"></i>
						</button>
					</div>

					<div class="prose max-w-none">
						<div
							class="p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500"
						>
							<p
								class="text-base text-gray-800 leading-relaxed mb-0"
							>
								{{ selectedNotification.notification }}
							</p>
						</div>
					</div>

					<div
						class="flex justify-end mt-6 pt-4 border-t border-gray-200"
					>
						<button
							class="px-6 py-2 text-base font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-4 focus:ring-gray-200 transition-all"
							@click="closeModal()"
						>
							Close
						</button>
					</div>
				</div>
			</template>
		</Modal>

		<!-- Main Content -->
		<div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
			<!-- Notifications List -->
			<div class="lg:col-span-3">
				<div
					class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
				>
					<!-- Filter Bar -->
					<div class="p-4 lg:p-6 border-b border-gray-200 bg-gray-50">
						<div
							class="flex flex-wrap items-center justify-between gap-4"
						>
							<div class="flex items-center gap-3">
								<button
									class="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
								>
									All
								</button>
								<button
									class="px-4 py-2 text-sm font-medium text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
								>
									Unread
								</button>
								<button
									class="px-4 py-2 text-sm font-medium text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
								>
									Important
								</button>
							</div>

							<div class="flex items-center gap-2">
								<button
									class="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
									title="Mark all as read"
								>
									<i class="fas fa-check-double text-sm"></i>
								</button>
								<button
									class="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
									title="Refresh"
									@click="refreshNotifications"
								>
									<i class="fas fa-sync-alt text-sm"></i>
								</button>
							</div>
						</div>
					</div>

					<!-- Notifications List -->
					<div
						v-if="notifications.length > 0"
						class="max-h-[600px] overflow-y-auto"
					>
						<div
							v-for="(notification, index) in notifications"
							:key="index"
							class="p-4 lg:p-6 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-all group"
							@click="openModal(notification)"
						>
							<div class="flex items-start gap-4">
								<!-- Notification Icon -->
								<div
									class="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors"
								>
									<i
										class="fas fa-bell text-blue-600 text-lg"
									></i>
								</div>

								<!-- Notification Content -->
								<div class="flex-1 min-w-0">
									<div
										class="flex items-start justify-between gap-4 mb-2"
									>
										<h4
											class="text-base lg:text-lg font-semibold text-gray-900 truncate"
										>
											{{ notification.subject }}
										</h4>
										<div
											class="flex items-center gap-2 flex-shrink-0"
										>
											<span class="text-xs text-gray-500">
												{{
													moment(
														notification.notification_date,
													).format('MMM DD')
												}}
											</span>
											<div
												class="w-2 h-2 bg-blue-500 rounded-full"
												v-if="!notification.read"
											></div>
										</div>
									</div>

									<p
										class="text-sm lg:text-base text-gray-600 leading-relaxed line-clamp-2 mb-2"
									>
										{{ notification.notification }}
									</p>

									<div
										class="flex items-center justify-between"
									>
										<div
											class="flex items-center gap-2 text-xs text-gray-500"
										>
											<i class="fas fa-clock"></i>
											<span>{{
												moment(
													notification.notification_date,
												).fromNow()
											}}</span>
										</div>

										<div
											class="opacity-0 group-hover:opacity-100 transition-opacity"
										>
											<i
												class="fas fa-arrow-right text-blue-600 text-sm"
											></i>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Empty State -->
					<div v-else class="p-8 lg:p-12 text-center">
						<div
							class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
						>
							<i
								class="fas fa-bell-slash text-gray-400 text-2xl"
							></i>
						</div>
						<h3 class="text-lg font-semibold text-gray-900 mb-2">
							No Notifications Found
						</h3>
						<p class="text-gray-500 mb-6">
							You're all caught up! New notifications will appear
							here.
						</p>
						<button
							class="px-6 py-3 text-base font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 focus:ring-4 focus:ring-blue-200 transition-all"
							@click="refreshNotifications"
						>
							<i class="fas fa-sync-alt mr-2"></i>
							Refresh
						</button>
					</div>
				</div>
			</div>

			<!-- Sidebar -->
			<div class="lg:col-span-1 space-y-6">
				<!-- Quick Actions -->
				<div
					class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6"
				>
					<h3
						class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"
					>
						<i class="fas fa-bolt text-blue-600"></i> Quick Actions
					</h3>
					<div class="space-y-3">
						<button
							class="w-full p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors flex items-center gap-3 text-left"
							@click="markAllAsRead"
						>
							<div
								class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center"
							>
								<i class="fas fa-check-double text-xs"></i>
							</div>
							<span class="text-sm font-medium text-gray-800"
								>Mark All Read</span
							>
						</button>
						<button
							class="w-full p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors flex items-center gap-3 text-left"
						>
							<div
								class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center"
							>
								<i class="fas fa-cog text-xs"></i>
							</div>
							<span class="text-sm font-medium text-gray-800"
								>Settings</span
							>
						</button>
					</div>
				</div>

				<!-- Notification Summary -->
				<div
					class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6"
				>
					<h3
						class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"
					>
						<i class="fas fa-chart-bar text-blue-600"></i> Summary
					</h3>
					<div class="space-y-4">
						<div
							class="flex items-center justify-between py-2 border-b border-gray-200"
						>
							<span class="text-sm text-gray-600">Total</span>
							<span class="text-lg font-bold text-gray-900">{{
								notifications.length
							}}</span>
						</div>
						<div
							class="flex items-center justify-between py-2 border-b border-gray-200"
						>
							<span class="text-sm text-gray-600">Unread</span>
							<span class="text-lg font-bold text-blue-600">{{
								unreadCount
							}}</span>
						</div>
						<div class="flex items-center justify-between py-2">
							<span class="text-sm text-gray-600">This Week</span>
							<span class="text-lg font-bold text-green-600">{{
								weeklyCount
							}}</span>
						</div>
					</div>
				</div>

				<!-- Tips Card -->
				<div
					class="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 p-4 lg:p-6"
				>
					<div class="flex items-center gap-3 mb-3">
						<div
							class="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center"
						>
							<i class="fas fa-lightbulb text-base"></i>
						</div>
						<h3 class="text-lg font-semibold text-gray-900">
							Pro Tip
						</h3>
					</div>
					<p class="text-sm text-gray-700 leading-relaxed">
						Enable push notifications to stay updated with important
						alerts and never miss critical updates.
					</p>
					<button
						class="w-full mt-4 py-2 text-sm font-medium text-blue-600 bg-white rounded-lg hover:bg-blue-50 transition-colors border border-gray-200"
					>
						Learn More
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import notifications from '../rsc/stores/notifications.store';
import { mapGetters, mapActions } from 'vuex';

export default {
	name: 'Notifications',
	beforeCreate() {
		Event.$emit('layout:loading:start', 'Loading notifications...');
	},
	beforeDestroy() {
		this.$store.unregisterModule('notifications');
	},
	created() {
		this.$store.registerModule('notifications', notifications);
	},
	components: {
		NotificationItem: () => import('../templates/others/notification-item'),
		Modal: () => import('../templates/others/modal'),
	},
	data() {
		return {
			moment,
			loading: false,
		};
	},
	computed: {
		...mapGetters([
			'notifications',
			'selectedNotification',
			'showNotification',
		]),
		unreadCount() {
			return this.notifications.filter((n) => !n.read).length;
		},
		weeklyCount() {
			const weekAgo = moment().subtract(7, 'days');
			return this.notifications.filter((n) =>
				moment(n.notification_date).isAfter(weekAgo),
			).length;
		},
	},
	methods: {
		...mapActions(['fetchData', 'openModal', 'closeModal']),

		async refreshNotifications() {
			this.loading = true;
			try {
				await this.fetchData();
			} catch (error) {
				console.error('Error refreshing notifications:', error);
			} finally {
				this.loading = false;
			}
		},

		markAllAsRead() {
			// Implement mark all as read functionality
			Event.$emit('notifications:show', {
				type: 'info',
				title: 'Feature Coming Soon',
				message:
					'Mark all as read functionality will be implemented soon',
				duration: 3000,
			});
		},
	},
	async mounted() {
		try {
			await this.fetchData();
		} catch (error) {
			console.error('Error loading notifications:', error);
		} finally {
			Event.$emit('layout:loading:stop');
		}
	},
};
</script>

<style scoped>
/* Line clamp utility */
.line-clamp-2 {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

/* Custom scrollbar for webkit browsers */
.overflow-y-auto::-webkit-scrollbar {
	width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
	background: #f8fafc;
	border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}

/* Focus styles for accessibility */
button:focus-visible {
	outline: 2px solid #3b82f6;
	outline-offset: 2px;
	border-radius: 0.375rem;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
	.transition-all,
	.transition-colors,
	.transition-opacity {
		transition: none;
	}
}

/* High contrast support */
@media (prefers-contrast: high) {
	.border-gray-200 {
		border-color: #000;
	}

	.text-gray-900 {
		color: #000;
	}

	.text-gray-600,
	.text-gray-500 {
		color: #333;
	}
}

/* Mobile optimizations */
@media (max-width: 640px) {
	.cursor-pointer {
		min-height: 44px;
	}
}
</style>
